/**
 * GlobalMessagesBell — R2: Global messages icon in DashboardLayout header.
 *
 * Shows total unread count across all projects. Click opens a popover
 * listing projects with unread messages. Clicking a project navigates to
 * that project's page with the messages section pre-expanded.
 *
 * Per-project MessagesSlideOver is unaffected — this is additive only.
 */
import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNavigate } from 'react-router-dom';
import { useGlobalUnreadMessages, type ProjectMessageSummary } from '@/hooks/useGlobalMessages';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';

// ============================================================================
// Helpers
// ============================================================================

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ============================================================================
// Project Row
// ============================================================================

interface ProjectRowProps {
  summary: ProjectMessageSummary;
  onNavigate: (projectId: string) => void;
}

function ProjectRow({ summary, onNavigate }: ProjectRowProps) {
  return (
    <button
      className="w-full text-left px-3 py-2.5 hover:bg-muted/50 transition-colors border-b last:border-b-0"
      onClick={() => onNavigate(summary.project_id)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {summary.project_name || 'Unnamed Project'}
            </span>
            {summary.unread_count > 0 && (
              <Badge
                variant="destructive"
                className="h-4 min-w-4 flex items-center justify-center p-0 px-1 text-[10px] shrink-0"
              >
                {summary.unread_count > 9 ? '9+' : summary.unread_count}
              </Badge>
            )}
          </div>
          {summary.latest_message_content && (
            <p className="text-xs text-muted-foreground truncate mt-0.5 max-w-[240px]">
              {summary.latest_sender_email
                ? `${summary.latest_sender_email.split('@')[0]}: `
                : ''}
              {summary.latest_message_content}
            </p>
          )}
        </div>
        {summary.latest_message_at && (
          <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">
            {formatTime(summary.latest_message_at)}
          </span>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function GlobalMessagesBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useGlobalUnreadMessages();
  const [open, setOpen] = useState(false);

  const totalUnread = data?.total_unread ?? 0;
  const summary = data?.summary ?? [];

  const handleNavigate = (projectId: string) => {
    setOpen(false);
    // Navigate to project detail with messages section pre-expanded
    navigate(`/projects/${projectId}?section=messages`);
  };

  const handleRefresh = () => {
    queryClient.refetchQueries({ queryKey: ['global-messages-unread'] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Messages${totalUnread > 0 ? ` (${totalUnread} unread)` : ''}`}
        >
          <MessageCircle className="h-4 w-4" />
          {totalUnread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {totalUnread > 9 ? '9+' : totalUnread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">Messages</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              className="h-6 w-6"
              aria-label="Refresh messages"
            >
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
          {totalUnread > 0 && (
            <span className="text-xs text-muted-foreground">
              {totalUnread} unread
            </span>
          )}
        </div>

        {/* Content */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : summary.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No unread messages
            </div>
          ) : (
            summary.map((item) => (
              <ProjectRow
                key={item.project_id}
                summary={item}
                onNavigate={handleNavigate}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
